/**
 * TaskActions - Action buttons for task interaction.
 *
 * Shows submit/confirm button and optional secondary actions.
 */

import React from 'react';
import { Box, Typography, Button, CircularProgress } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { colors } from '../../theme/colors';

export interface TaskActionsProps {
  isReady: boolean;
  isSubmitting?: boolean;
  onSubmit: () => void;
  submitLabel?: string;
  submittingLabel?: string;
  readyMessage?: string;
  readySubMessage?: string;
  secondaryAction?: React.ReactNode;
}

export const TaskActions: React.FC<TaskActionsProps> = ({
  isReady,
  isSubmitting = false,
  onSubmit,
  submitLabel = 'Confirm & Submit',
  submittingLabel = 'Confirming...',
  readyMessage = 'All required information collected!',
  readySubMessage = 'Click confirm to submit your response.',
  secondaryAction,
}) => {
  if (!isReady) {
    return secondaryAction ? (
      <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        {secondaryAction}
      </Box>
    ) : null;
  }

  return (
    <Box
      sx={{
        p: 2,
        bgcolor: colors.green[50],
        borderTop: 1,
        borderColor: 'divider',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="subtitle2" sx={{ color: colors.green[700], fontWeight: 600 }}>
            {readyMessage}
          </Typography>
          <Typography variant="caption" sx={{ color: colors.green[600] }}>
            {readySubMessage}
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="success"
          onClick={onSubmit}
          disabled={isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />}
          sx={{ minWidth: 150 }}
        >
          {isSubmitting ? submittingLabel : submitLabel}
        </Button>
      </Box>
    </Box>
  );
};

export default TaskActions;
